import { LineChart, Line, XAxis, CartesianGrid, Tooltip, YAxis } from 'recharts'

import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select'
import { AnalyticsPeriodResult, GetAnalyticsResponse } from '@/store/analytic.types'
import {
  formatDateLocalized,
  formatDuration,
  getDefaultReferenceDate,
  getSortedPeriods,
  isAnalyticsPeriodResult,
  Period,
  PERIOD_LABELS,
} from '@/pages/dashboard/shared/analytics-utils'

interface Props {
  period: Period
  setPeriod: (p: Period) => void
  referenceDate: Date
  setReferenceDate: (d: Date) => void
  data: GetAnalyticsResponse | undefined
  dateStart: string
  canShiftForward: (d: Date) => boolean
  shiftDate: (d: Date, p: Period, n: number) => Date
  periods: readonly Period[]
}

export const DurationChartCard = ({
  period,
  setPeriod,
  referenceDate,
  setReferenceDate,
  data,
  dateStart,
  canShiftForward,
  shiftDate,
  periods,
}: Props): JSX.Element => {
  const validData: AnalyticsPeriodResult | null = isAnalyticsPeriodResult(data) ? data : null
  const sortedPeriods = validData ? getSortedPeriods(validData, period) : []

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-center w-full gap-6 flex-wrap text-center">
          <div className="flex flex-col">
            <CardTitle className="text-base">Prosečno trajanje razgovora i broj poruka</CardTitle>
            <CardDescription className="text-sm">
              {PERIOD_LABELS[period]}, {formatDateLocalized(dateStart)}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setReferenceDate(shiftDate(referenceDate, period, -1))}
              className="rounded border px-2 py-1"
            >
              &lt;
            </button>
            <button
              disabled={!canShiftForward(shiftDate(referenceDate, period, +1))}
              onClick={() => {
                const next = shiftDate(referenceDate, period, +1)
                if (canShiftForward(next)) setReferenceDate(next)
              }}
              className="rounded border px-2 py-1 disabled:opacity-40"
            >
              &gt;
            </button>
            <Select
              value={period}
              onValueChange={(value) => {
                const newPeriod = value as Period
                setReferenceDate(getDefaultReferenceDate(newPeriod))
                setPeriod(newPeriod)
              }}
            >
              <SelectTrigger className="w-[120px] text-foreground bg-background">
                <SelectValue placeholder="Izaberi period" />
              </SelectTrigger>
              <SelectContent>
                {periods.map((p) => (
                  <SelectItem key={p} value={p}>
                    {PERIOD_LABELS[p]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent className="relative flex justify-center items-center" style={{ height: 300 }}>
        {!validData || validData.periods.length === 0 ? (
          <div className="absolute inset-0 flex justify-center items-center text-muted-foreground text-lg select-none">
            Nema podataka
          </div>
        ) : (
          <LineChart
            width={800}
            height={300}
            data={sortedPeriods}
            margin={{ top: 20, right: 20, left: 30, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="2 10" />
            <XAxis
              dataKey="date"
              interval={0}
              tickFormatter={(value: string, index: number) => {
                if (period !== Period.Monthly) {
                  return new Date(value).toLocaleDateString('sr-RS', {
                    day: '2-digit',
                    month: '2-digit',
                  })
                }

                return index % 5 === 0
                  ? new Date(value).toLocaleDateString('sr-RS', {
                      day: '2-digit',
                      month: '2-digit',
                    })
                  : ''
              }}
            />
            <YAxis interval="preserveStartEnd" tickCount={5} />

            <Tooltip
              content={({ active, payload, label }) => {
                if (!active || !payload || payload.length === 0) return null
                return (
                  <div className="rounded border bg-background px-3 py-2 text-foreground shadow-sm text-sm">
                    <div className="font-medium">{formatDateLocalized(label)}</div>
                    {payload.map((entry, i) => (
                      <div key={i}>
                        {entry.dataKey === 'average_thread_duration' && (
                          <>Prosečno trajanje razgovora: {formatDuration(Number(entry.value))}</>
                        )}
                        {entry.dataKey === 'average_thread_messages' && (
                          <>Prosečan broj poruka: {entry.value}</>
                        )}
                      </div>
                    ))}
                  </div>
                )
              }}
            />

            <Line type="monotone" dataKey="average_thread_duration" stroke="#8884d8" />
            <Line type="monotone" dataKey="average_thread_messages" stroke="#82ca9d" />
          </LineChart>
        )}
      </CardContent>
    </Card>
  )
}
